import { Minus, Plus } from "lucide-react";
import { cn } from "./cn.js";

type Props = {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  /** Step. A fractional step (e.g. 0.01 — xG) does not floor the value to an integer. */
  step?: number;
  /** Visual scale — "lg" is the chunky one for the bay score. */
  size?: "sm" | "md" | "lg";
  label?: string;
  className?: string;
};

const sizeMap = {
  sm: { btn: "h-7 w-7", input: "h-7 w-10 text-sm" },
  md: { btn: "h-8 w-8", input: "h-8 w-14 text-base" },
  lg: { btn: "h-12 w-12", input: "h-14 w-20 text-3xl" },
};

/** With an integer step it rounds down (the old behavior), while with a fractional
 *  step it rounds to the step's precision — so that floating-point residue such
 *  as 0.1 + 0.2 does not leak into the value. */
function snapToStep(n: number, step: number): number {
  if (step >= 1) return Math.floor(n);
  const decimals = (String(step).split(".")[1] ?? "").length;
  return Number(n.toFixed(decimals));
}

/** Generic numeric stepper. Defaults clamp to [0, ∞). Used by score, stats,
 *  additional-time, anything numeric. */
export function Stepper({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  size = "md",
  label,
  className,
}: Props) {
  const sz = sizeMap[size];
  const set = (n: number) => {
    const clamped = Math.max(min, max !== undefined ? Math.min(max, n) : n);
    onChange(snapToStep(clamped, step));
  };
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <span className="text-[11px] uppercase tracking-[0.08em] text-ink-muted font-medium">
          {label}
        </span>
      )}
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          aria-label="kamaytirish"
          onClick={() => set(value - step)}
          className={cn(
            "grid place-items-center rounded-md border border-border bg-surface-elevated",
            "text-ink-soft transition-colors hover:bg-surface-hover hover:text-ink",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60",
            sz.btn,
          )}
        >
          <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
        <input
          type="number"
          value={value}
          onChange={(e) => set(Number(e.target.value) || 0)}
          min={min}
          step={step}
          {...(max !== undefined ? { max } : {})}
          className={cn(
            "rounded-md border border-border bg-surface text-center font-display font-bold tabular-nums",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60",
            sz.input,
          )}
        />
        <button
          type="button"
          aria-label="oshirish"
          onClick={() => set(value + step)}
          className={cn(
            "grid place-items-center rounded-md border border-border bg-surface-elevated",
            "text-ink-soft transition-colors hover:bg-surface-hover hover:text-ink",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60",
            sz.btn,
          )}
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
